import React, { type ReactNode } from 'react';
import { useShowCompactMode } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { CARD_LIST_DROPPABLE_TYPE } from '../../../common/constants/index.tsx';
import type { CardListId } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getIsCompanyManaged } from '../../../state/selectors/app/index.tsx';
import {
	getCardIdWithAboveICCOpen,
	makeOrderedVisibleCardEntries,
	makeOrderVisibleCardIdsForCardListWithLimit,
	makeGetFastVirtualListMinimumOffSetByCardListId,
	makeGetDefaultRowSize,
	getDefaultCardHeight,
	makeOrderedVisibleCardEntriesOld,
	getExpandedCardGroups,
	makeOrderedVisibleCardIdsForCardList,
	makeGetVisibleCardOrderFromCardEntries,
} from '../../../state/selectors/cards/index.tsx';
import type { RenderRowFn } from './types.tsx';
import { FastVirtualList } from './view.tsx';

export type Props = {
	cardListId: CardListId;
	emptyList: ReactNode;
	isDropDisabled: boolean;
	renderCard: RenderRowFn;
};

const FastVirtualListContainer = ({ isDropDisabled, cardListId, emptyList, renderCard }: Props) => {
	const cardIdWithAboveIccOpen = useBacklogSelector(getCardIdWithAboveICCOpen);
	const getDefaultRowSize = useBacklogSelector(makeGetDefaultRowSize);
	const showCompactMode = useShowCompactMode();
	const defaultCardHeight = useBacklogSelector((state) =>
		getDefaultCardHeight(state)(showCompactMode),
	);
	const isCmpBacklog = useBacklogSelector(getIsCompanyManaged);
	const cardEntries = useBacklogSelector((state) =>
		isCmpBacklog
			? makeOrderedVisibleCardEntries(state)(cardListId)
			: makeOrderedVisibleCardEntriesOld(state)(cardListId),
	);
	const cardIds = useBacklogSelector((state) =>
		makeGetVisibleCardOrderFromCardEntries(state)(cardListId),
	);
	const visibleCardAmount = useBacklogSelector((state) =>
		__SERVER__
			? makeOrderVisibleCardIdsForCardListWithLimit(state)(cardListId).length
			: makeOrderedVisibleCardIdsForCardList(state)(cardListId).length,
	);
	const minimumOffset = useBacklogSelector((state) =>
		makeGetFastVirtualListMinimumOffSetByCardListId(state)(cardListId),
	);

	const expandedCardGroups = useBacklogSelector(getExpandedCardGroups);

	return (
		<FastVirtualList
			cardIdWithAboveIccOpen={cardIdWithAboveIccOpen}
			cardEntries={cardEntries}
			visibleCardAmount={visibleCardAmount}
			minimumOffset={minimumOffset}
			isDropDisabled={isDropDisabled}
			getDefaultRowSize={getDefaultRowSize}
			defaultCardHeight={defaultCardHeight}
			cardListId={cardListId}
			emptyList={emptyList}
			renderCard={renderCard}
			expandedCardGroups={expandedCardGroups}
			droppableType={CARD_LIST_DROPPABLE_TYPE}
			cardIds={cardIds}
		/>
	);
};

export default FastVirtualListContainer;
